import React, { useEffect, useState } from "react";
import { Loader2, UserPlus, LogIn, Sparkles, ShieldCheck, Eye, EyeOff, User, Briefcase, Check, X as XIcon, ArrowLeft, Mail, KeyRound, RefreshCw } from "lucide-react";
import { useAuth } from "./AuthContext";
import { api } from "./api";
import BetaChip from "./BetaChip";

export default function LoginScreen({ initialMode = "login", onBack }) {
  const [mode, setMode] = useState(initialMode);
  // "password" or "otp" — only relevant when mode==="login"
  const [loginMethod, setLoginMethod] = useState("password");
  const { login, signup } = useAuth();
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [accountType, setAccountType] = useState("individual");
  const [companyName, setCompanyName] = useState("");
  const [plan, setPlan] = useState("individual");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [phone, setPhone] = useState("");

  // Email OTP verification (signup)
  const [email, setEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpErr, setOtpErr] = useState("");

  // Login-OTP specific state
  const [loginOtpEmail, setLoginOtpEmail] = useState("");
  const [loginOtpSent, setLoginOtpSent] = useState(false);
  const [loginOtpCode, setLoginOtpCode] = useState("");
  const [loginOtpBusy, setLoginOtpBusy] = useState(false);
  const [loginOtpErr, setLoginOtpErr] = useState("");

  // Keep plan tier consistent with account type:
  //  - Individual account has no tier selector (plan locked to "individual").
  //  - Business account must pick one of the business tiers (default "startup").
  const chooseAccountType = (t) => {
    setAccountType(t);
    if (t === "individual") setPlan("individual");
    else if (plan === "individual") setPlan("startup");
  };
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Live availability check (signup only). Triggers on debounce.
  const [avail, setAvail] = useState(null); // null | { available, reason }
  useEffect(() => {
    let cancelled = false;
    if (mode !== "signup") { setAvail(null); return; }
    const u = (username || "").trim();
    if (!u || u.includes("@")) { setAvail(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/check-username/${encodeURIComponent(u)}`);
        if (!cancelled) setAvail(data);
      } catch {
        if (!cancelled) setAvail(null);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [username, mode]);

  const formatErr = (e) => {
    const d = e?.response?.data?.detail;
    if (!d) return "Something went wrong.";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join(" ");
    return String(d);
  };

  const sendOtp = async () => {
    setOtpErr("");
    const em = email.trim().toLowerCase();
    if (!em || !em.includes("@") || !em.split("@")[1]?.includes(".")) {
      setOtpErr("Enter a valid email address first.");
      return;
    }
    setOtpBusy(true);
    try {
      await api.post("/auth/signup/send-otp", { email: em });
      setOtpSent(true);
    } catch (e) {
      setOtpErr(formatErr(e));
    }
    setOtpBusy(false);
  };

  const verifyOtp = async () => {
    setOtpErr("");
    if (!otpCode.trim()) { setOtpErr("Enter the 6-digit code."); return; }
    setOtpBusy(true);
    try {
      await api.post("/auth/signup/verify-otp", { email: email.trim().toLowerCase(), code: otpCode.trim() });
      setOtpVerified(true);
    } catch (e) {
      setOtpErr(formatErr(e));
    }
    setOtpBusy(false);
  };

  const canSubmitSignup = username.trim() && password && otpVerified && acceptedTerms && phone.trim() &&
    (accountType !== "business" || companyName.trim());

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (mode === "signup" && !canSubmitSignup) {
      setErr(!otpVerified ? "Please verify your email first." : !acceptedTerms ? "Please accept the Privacy Policy and Terms & Conditions." : "Please fill in all required fields.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "login") await login(username, password);
      else {
        const ref = localStorage.getItem("hexa_ref_code") || "";
        await signup(username, name, password, accountType, companyName, ref, plan, email.trim().toLowerCase(), acceptedTerms, phone.trim());
        if (ref) localStorage.removeItem("hexa_ref_code");
      }
    } catch (e) {
      setErr(formatErr(e));
    }
    setBusy(false);
  };

  return (
    <div
      data-testid="login-screen"
      className="fixed inset-0 z-[1500] flex items-center justify-center hex-grid-bg"
      style={{
        backgroundImage:
          "linear-gradient(rgba(4,6,16,0.72), rgba(4,6,16,0.72) 25%, rgba(4,6,16,0.72) 75%, rgba(4,6,16,0.72)), url(" +
          (process.env.PUBLIC_URL || "") +
          "/desktop-wallpaper.jpg)",
      }}
    >
      {/* corner telemetry */}
      <div className="absolute top-4 left-5 mono text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/40">
        // node://hexa-01 · auth-gateway
      </div>
      <div className="absolute top-4 right-5 mono text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/40 flex items-center gap-2">
        <span className="w-1.5 h-1.5 bg-[#8b5cf6] rounded-full animate-pulse" />
        secure-channel · online
      </div>
      {onBack && (
        <button
          data-testid="login-back-home"
          onClick={onBack}
          className="absolute top-12 left-5 mono text-[10.5px] uppercase tracking-[0.28em] text-white flex items-center gap-1.5 transition hover:text-[#a78bfa]"
          style={{
            border: "1px solid rgba(139,92,246,0.55)",
            background: "rgba(139,92,246,0.12)",
            borderRadius: 2,
            padding: "8px 14px",
            boxShadow: "0 4px 14px rgba(139,92,246,0.25)",
          }}
        >
          <ArrowLeft size={12} /> Back to Home
        </button>
      )}

      <div
        className="w-[440px] max-w-[92vw] max-h-[92vh] overflow-y-auto panel-strong p-6 float-in relative"
        style={{
          borderRadius: 2,
          clipPath: "polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px))",
          boxShadow: "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(139,92,246,0.18) inset",
        }}
      >
        <div className="flex flex-col items-center mb-5 relative">
          <img
            src="/hexa-logo.png"
            alt="HEXA — AI Web Operating System"
            style={{
              height: 220,
              width: "auto",
              clipPath: "inset(0 0 16% 0)",
              marginBottom: "calc(220px * -0.16)",
            }}
          />
          <div className="absolute right-[-6px] top-1">
            <BetaChip size="xs" tone="outline" />
          </div>
          <div className="text-[9px] uppercase tracking-[0.4em] text-[#8b5cf6]/60 mono mt-2">// one identity · every app</div>
        </div>

        <div className="flex gap-1 bg-[#0e1a22] p-1 mb-4" style={{ border: "1px solid rgba(139,92,246,0.15)" }}>
          <button
            data-testid="auth-tab-login"
            onClick={() => { setMode("login"); setErr(""); }}
            className={`flex-1 py-2 text-[10px] font-medium uppercase tracking-[0.3em] mono ${mode === "login" ? "bg-[#8b5cf6]/10 text-[#8b5cf6]" : "text-[#c7c1ff]/40"}`}
          >Sign In</button>
          <button
            data-testid="auth-tab-signup"
            onClick={() => { setMode("signup"); setErr(""); }}
            className={`flex-1 py-2 text-[10px] font-medium uppercase tracking-[0.3em] mono ${mode === "signup" ? "bg-[#8b5cf6]/10 text-[#8b5cf6]" : "text-[#c7c1ff]/40"}`}
          >Create ID</button>
        </div>

        {/* Login method sub-tabs (only in Sign In mode) */}
        {mode === "login" && (
          <div className="flex gap-1 mb-4 p-1 bg-[#0e1a22]/50" style={{ border: "1px solid rgba(139,92,246,0.10)" }}>
            <button
              data-testid="login-method-password"
              onClick={() => { setLoginMethod("password"); setErr(""); setLoginOtpErr(""); }}
              className={`flex-1 py-1.5 text-[9px] font-medium uppercase tracking-[0.25em] mono flex items-center justify-center gap-1.5 transition-all
                ${loginMethod === "password" ? "bg-[#8b5cf6]/15 text-[#a78bfa]" : "text-[#c7c1ff]/35 hover:text-[#c7c1ff]/60"}`}
            >
              <KeyRound size={10} /> Login with Password
            </button>
            <button
              data-testid="login-method-otp"
              onClick={() => { setLoginMethod("otp"); setErr(""); setLoginOtpErr(""); }}
              className={`flex-1 py-1.5 text-[9px] font-medium uppercase tracking-[0.25em] mono flex items-center justify-center gap-1.5 transition-all
                ${loginMethod === "otp" ? "bg-[#3b82f6]/15 text-[#60a5fa]" : "text-[#c7c1ff]/35 hover:text-[#c7c1ff]/60"}`}
            >
              <Mail size={10} /> Login with OTP
            </button>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          {/* OTP LOGIN FORM */}
          {mode === "login" && loginMethod === "otp" && (
            <div className="space-y-3">
              <div>
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/50 mb-1 mono">registered email address</div>
                <input
                  data-testid="login-otp-email-input"
                  type="email"
                  value={loginOtpEmail}
                  onChange={e => setLoginOtpEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-[#0e1a22] border border-[#8b5cf6]/20 focus:border-[#3b82f6]/60 px-3 py-2 text-sm outline-none mono"
                />
              </div>
              {!loginOtpSent ? (
                <button
                  data-testid="login-otp-send-btn"
                  type="button"
                  disabled={loginOtpBusy || !loginOtpEmail.trim()}
                  onClick={async () => {
                    setLoginOtpBusy(true); setLoginOtpErr("");
                    try {
                      await api.post("/auth/login/send-otp", { email: loginOtpEmail.trim() });
                      setLoginOtpSent(true);
                    } catch (e) {
                      setLoginOtpErr(e?.response?.data?.detail || "Failed to send OTP");
                    }
                    setLoginOtpBusy(false);
                  }}
                  className="w-full py-2.5 text-[11px] uppercase tracking-[0.3em] font-semibold flex items-center justify-center gap-2 mono disabled:opacity-50"
                  style={{ background: "linear-gradient(90deg, #3b82f6, #06b6d4)", color: "#0b1014" }}
                >
                  {loginOtpBusy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                  Send OTP to Email
                </button>
              ) : (
                <>
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/50 mb-1 mono flex items-center justify-between">
                      <span>6-digit OTP</span>
                      <button
                        type="button"
                        className="text-[#3b82f6] hover:text-[#60a5fa] flex items-center gap-0.5"
                        onClick={async () => {
                          setLoginOtpBusy(true); setLoginOtpErr("");
                          try { await api.post("/auth/login/send-otp", { email: loginOtpEmail.trim() }); } catch { /* ignore */ }
                          setLoginOtpBusy(false);
                        }}
                      ><RefreshCw size={8} /> resend</button>
                    </div>
                    <input
                      data-testid="login-otp-code-input"
                      value={loginOtpCode}
                      onChange={e => setLoginOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="_ _ _ _ _ _"
                      maxLength={6}
                      className="w-full bg-[#0e1a22] border border-[#3b82f6]/25 focus:border-[#3b82f6]/70 px-3 py-2 text-center text-xl tracking-[0.5em] outline-none mono"
                    />
                    <div className="mono text-[9px] text-[#c7c1ff]/40 mt-1">OTP sent to {loginOtpEmail}</div>
                  </div>
                  {loginOtpErr && <div className="text-xs text-[#f87171] mono">{loginOtpErr}</div>}
                  <button
                    data-testid="login-otp-verify-btn"
                    type="button"
                    disabled={loginOtpBusy || loginOtpCode.length < 6}
                    onClick={async () => {
                      setLoginOtpBusy(true); setLoginOtpErr(""); setErr("");
                      try {
                        const { data } = await api.post("/auth/login/verify-otp", { email: loginOtpEmail.trim(), otp: loginOtpCode });
                        if (data.token) {
                          localStorage.setItem("aura_token", data.token);
                          window.location.reload();
                        }
                      } catch (e) {
                        setLoginOtpErr(e?.response?.data?.detail || "Invalid OTP");
                      }
                      setLoginOtpBusy(false);
                    }}
                    className="w-full py-2.5 text-[11px] uppercase tracking-[0.3em] font-semibold flex items-center justify-center gap-2 mono disabled:opacity-50"
                    style={{ background: "linear-gradient(90deg, #3b82f6, #22d3ee)", color: "#0b1014" }}
                  >
                    {loginOtpBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    Verify &amp; Sign In
                  </button>
                </>
              )}
            </div>
          )}

          {/* PASSWORD LOGIN FORM (existing, now only shown when loginMethod === "password" or signup) */}
          {(mode === "signup" || (mode === "login" && loginMethod === "password")) && (
            <>
              {/* Signup-only fields — hidden in login mode */}
              {mode === "signup" && (
              <>
              <div>
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/50 mb-1 mono">account type</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="acct-type-individual"
                    onClick={() => chooseAccountType("individual")}
                    className={`flex flex-col items-start gap-1 p-3 mono text-left transition ${accountType === "individual" ? "bg-[#8b5cf6]/12 border-[#8b5cf6]/60 text-[#a78bfa]" : "bg-[#0e1a22] border-[#8b5cf6]/20 text-[#c7c1ff]/70 hover:border-[#8b5cf6]/40"}`}
                    style={{ border: "1px solid", borderColor: accountType === "individual" ? "rgba(139,92,246,0.6)" : "rgba(139,92,246,0.2)" }}
                  >
                    <User size={14} />
                    <span className="text-[11px] uppercase tracking-[0.2em] font-medium">Individual</span>
                    <span className="text-[9px] normal-case tracking-normal text-[#c7c1ff]/55">just for me</span>
                  </button>
                  <button
                    type="button"
                    data-testid="acct-type-business"
                    onClick={() => chooseAccountType("business")}
                    className={`flex flex-col items-start gap-1 p-3 mono text-left transition ${accountType === "business" ? "bg-[#3b82f6]/12 border-[#3b82f6]/60 text-[#60a5fa]" : "bg-[#0e1a22] border-[#8b5cf6]/20 text-[#c7c1ff]/70 hover:border-[#3b82f6]/40"}`}
                    style={{ border: "1px solid", borderColor: accountType === "business" ? "rgba(59,130,246,0.6)" : "rgba(139,92,246,0.2)" }}
                  >
                    <Briefcase size={14} />
                    <span className="text-[11px] uppercase tracking-[0.2em] font-medium">Business</span>
                    <span className="text-[9px] normal-case tracking-normal text-[#c7c1ff]/55">team & attendance</span>
                  </button>
                </div>
                <div className="mt-1.5 text-[9px] mono text-[#c7c1ff]/45 uppercase tracking-[0.15em]">
                  {accountType === "business"
                    ? "// boss role · add employees · attendance dashboard"
                    : "// solo workspace · no team / attendance access"}
                </div>
              </div>
              {/* Plan tier picker — business only, shown BEFORE company name */}
              {accountType === "business" && (
                <div>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/50 mb-1 mono">plan tier</div>
                  <div className="grid grid-cols-4 gap-1.5">
                    {[
                      { k: "startup",    label: "Startup",    hint: "< 10 seats",   color: "#f472b6" },
                      { k: "sme",        label: "SME",        hint: "10-50 seats",  color: "#22d3ee" },
                      { k: "msme",       label: "MSME",       hint: "50-250 seats", color: "#fb923c" },
                      { k: "enterprise", label: "Enterprise", hint: "unlimited",    color: "#22c55e" },
                    ].map(p => (
                      <button
                        key={p.k}
                        type="button"
                        data-testid={`plan-${p.k}`}
                        onClick={() => setPlan(p.k)}
                        className="flex flex-col items-center gap-0.5 p-2 mono text-center transition"
                        style={{
                          border: "1px solid",
                          borderColor: plan === p.k ? p.color : "rgba(139,92,246,0.2)",
                          background: plan === p.k ? `${p.color}12` : "#0e1a22",
                          color: plan === p.k ? p.color : "rgba(199,193,255,0.6)",
                        }}
                      >
                        <span className="text-[10px] uppercase tracking-[0.15em] font-semibold">{p.label}</span>
                        <span className="text-[8px] normal-case tracking-normal opacity-70">{p.hint}</span>
                      </button>
                    ))}
                  </div>
                  <div className="mt-1.5 text-[9px] mono text-[#c7c1ff]/45 uppercase tracking-[0.15em]">
                    // plan sets default hierarchy · you can edit roles later
                  </div>
                </div>
              )}
              <div>
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/50 mb-1 mono">
                  company name {accountType === "business" ? "" : "(optional)"}
                </div>
                <input
                  data-testid="auth-company"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Inc."
                  className="w-full bg-[#0e1a22] border border-[#8b5cf6]/20 px-3 py-2 text-sm outline-none focus:border-[#3b82f6]/60 mono"
                />
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/50 mb-1 mono">display name</div>
                <input
                  data-testid="auth-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="your name"
                  className="w-full bg-[#0e1a22] border border-[#8b5cf6]/20 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono"
                />
              </div>

              <div>
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/50 mb-1 mono">mobile number</div>
                <input
                  data-testid="auth-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 98765 43210"
                  className="w-full bg-[#0e1a22] border border-[#8b5cf6]/20 px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/60 mono"
                />
              </div>

              <div>
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/50 mb-1 mono">email address</div>
                <div className="flex items-center gap-1.5">
                  <div className="flex-1 flex items-center bg-[#0e1a22] border border-[#8b5cf6]/20 focus-within:border-[#8b5cf6]/60">
                    <Mail size={12} className="ml-2.5 text-[#8b5cf6]/50" />
                    <input
                      data-testid="signup-email-input"
                      type="email"
                      value={email}
                      disabled={otpVerified}
                      onChange={(e) => { setEmail(e.target.value); setOtpSent(false); setOtpVerified(false); }}
                      placeholder="you@email.com"
                      className="flex-1 bg-transparent px-2.5 py-2 text-sm outline-none mono disabled:opacity-60"
                    />
                  </div>
                  {!otpVerified && (
                    <button
                      type="button"
                      data-testid="signup-send-otp"
                      onClick={sendOtp}
                      disabled={otpBusy}
                      className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] mono whitespace-nowrap"
                      style={{ border: "1px solid rgba(59,130,246,0.5)", color: "#60a5fa", background: "rgba(59,130,246,0.08)" }}
                    >
                      {otpBusy ? <Loader2 size={12} className="animate-spin" /> : (otpSent ? "resend" : "verify")}
                    </button>
                  )}
                  {otpVerified && (
                    <span className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] mono flex items-center gap-1 text-[#34d399]">
                      <Check size={12} /> verified
                    </span>
                  )}
                </div>
                {otpSent && !otpVerified && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <div className="flex-1 flex items-center bg-[#0e1a22] border border-[#8b5cf6]/20 focus-within:border-[#8b5cf6]/60">
                      <KeyRound size={12} className="ml-2.5 text-[#8b5cf6]/50" />
                      <input
                        data-testid="signup-otp-input"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="6-digit code"
                        className="flex-1 bg-transparent px-2.5 py-2 text-sm outline-none mono tracking-[0.3em]"
                      />
                    </div>
                    <button
                      type="button"
                      data-testid="signup-verify-otp"
                      onClick={verifyOtp}
                      disabled={otpBusy}
                      className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] mono whitespace-nowrap"
                      style={{ border: "1px solid rgba(34,211,153,0.5)", color: "#34d399", background: "rgba(34,211,153,0.08)" }}
                    >
                      {otpBusy ? <Loader2 size={12} className="animate-spin" /> : "verify"}
                    </button>
                  </div>
                )}
                {otpErr && <div className="mt-1.5 text-[10px] mono text-[#f87171]">{otpErr}</div>}
              </div>
              </>
              )}
              {/* === End signup-only fields === */}
          <div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/50 mb-1 mono">
              {mode === "login" ? "hexa id / email" : "username"}
            </div>
            <div className="flex items-center bg-[#0e1a22] border border-[#8b5cf6]/20 focus-within:border-[#8b5cf6]/60">
              <input
                data-testid={mode === "login" ? "login-email-input" : "signup-identity-input"}
                value={username}
                onChange={(e) => setUsername(mode === "signup" ? e.target.value.replace(/@.*/, "") : e.target.value)}
                placeholder={mode === "login" ? "you@hexawebos.com or your own email" : "choose a username"}
                className="flex-1 bg-transparent px-3 py-2 text-sm outline-none mono"
                autoFocus
              />
            </div>
            {mode === "signup" && avail && username.trim() && (
              <div
                data-testid="signup-username-availability"
                className={`mt-1 flex items-center gap-1.5 text-[10px] mono uppercase tracking-[0.2em] ${avail.available ? "text-[#34d399]" : "text-[#f87171]"}`}
              >
                {avail.available ? <Check size={11} /> : <XIcon size={11} />}
                {avail.reason}
              </div>
            )}
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/50 mb-1 mono">password</div>
            <div className="flex items-center bg-[#0e1a22] border border-[#8b5cf6]/20 focus-within:border-[#8b5cf6]/60">
              <input
                data-testid={mode === "login" ? "login-password-input" : "signup-password-input"}
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="flex-1 bg-transparent px-3 py-2 text-sm outline-none mono"
              />
              <button
                type="button"
                data-testid="auth-show-password-toggle"
                onClick={() => setShowPw(s => !s)}
                className="px-3 text-[#c7c1ff]/60 hover:text-[#8b5cf6]"
                title={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          {mode === "signup" && (
            <label
              data-testid="signup-accept-terms"
              className="flex items-start gap-2 text-[10px] mono text-[#c7c1ff]/70 cursor-pointer select-none"
            >
              <input
                type="checkbox"
                data-testid="signup-accept-terms-checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 accent-[#8b5cf6]"
              />
              <span>
                I accept the{" "}
                <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#a78bfa] underline hover:text-[#c7c1ff]">Privacy Policy</a>
                {" "}&{" "}
                <a href="/terms" target="_blank" rel="noreferrer" className="text-[#a78bfa] underline hover:text-[#c7c1ff]">Terms & Conditions</a>
              </span>
            </label>
          )}
          {err && <div className="text-xs text-[#f87171] mono">{err}</div>}
          <button
            data-testid={mode === "login" ? "login-submit-button" : "signup-submit-button"}
            type="submit"
            disabled={busy || (mode === "signup" && !canSubmitSignup)}
            className="w-full py-2.5 text-[11px] uppercase tracking-[0.3em] font-semibold flex items-center justify-center gap-2 mono disabled:opacity-50"
            style={{
              background: "linear-gradient(90deg, #8b5cf6, #3b82f6)",
              color: "#0b1014",
              clipPath: "polygon(8px 0, 100% 0, calc(100% - 8px) 100%, 0 100%)",
            }}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : mode === "login" ? <LogIn size={14} /> : <UserPlus size={14} />}
            {mode === "login" ? "authenticate" : "create identity"}
          </button>
            </>
          )}
        </form>

        <div className="mt-5 pt-4 border-t border-[#8b5cf6]/15 space-y-2 text-[10px] text-[#c7c1ff]/60 mono">
          <div className="flex items-center gap-2"><Sparkles size={11} color="#3b82f6" /> one id unifies mail · meetings · calendar · files · security</div>
          <div className="flex items-center gap-2"><ShieldCheck size={11} className="text-[#34d399]" /> bcrypt · jwt session · 7-day persistence</div>
        </div>
      </div>
    </div>
  );
}
